import React, { useState, useEffect, useRef } from 'react';

const CUSTOM_FILTER_SIZE_VALUE = '__custom__';
const MAX_FILTER_SIZE_LENGTH = 50;

// Standard HVAC filter sizes
const STANDARD_FILTER_SIZES = [
  '10x20x1',
  '12x12x1',
  '12x20x1',
  '12x24x1',
  '14x20x1',
  '14x24x1',
  '14x25x1',
  '16x20x1',
  '16x24x1',
  '16x25x1',
  '18x20x1',
  '18x24x1',
  '18x25x1',
  '20x20x1',
  '20x24x1',
  '20x25x1',
  '20x30x1',
  '24x24x1',
  '24x30x1',
  '25x25x1',
  '10x20x2',
  '12x24x2',
  '14x25x2',
  '16x25x2',
  '20x25x2',
  '10x20x4',
  '12x24x4',
  '14x25x4',
  '16x25x4',
  '20x25x4',
  '10x20x5',
  '12x24x5',
  '14x25x5',
  '16x25x5',
  '20x25x5',
];

const isStandardFilterSize = (size) => STANDARD_FILTER_SIZES.includes(size);

const isCustomFilterSize = (size) => Boolean(size) && !isStandardFilterSize(size);

const getCustomSizeFilterIds = (filters) => new Set(
  (filters || [])
    .filter((filter) => isCustomFilterSize(filter.filterSize))
    .map((filter) => filter.id)
);

// Base input styles
const baseInputStyles = "px-4 py-2.5 h-11 w-full border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 text-sm transition-colors";

// Select styles
const selectStyles = "px-4 py-2.5 h-11 w-full border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 text-sm transition-colors appearance-none bg-no-repeat bg-[right_0.5rem_center] bg-[length:1.5em_1.5em] bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236B7280%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M10%203a1%201%200%2001.707.293l3%203a1%201%200%2001-1.414%201.414L10%205.414%207.707%207.707a1%201%200%2001-1.414-1.414l3-3A1%201%200%200110%203zm-3.707%209.293a1%201%200%20011.414%200L10%2014.586l2.293-2.293a1%201%200%20011.414%201.414l-3%203a1%201%200%2001-1.414%200l-3-3a1%201%200%20010-1.414z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] pr-10";

// Helper function to normalize filters for comparison (ignores id)
const normalizeFiltersForComparison = (filters) => {
  if (!filters || filters.length === 0) return [];
  return filters.map(f => ({
    filterLocation: f.filterLocation || '',
    filterSize: f.filterSize || '',
  })).sort((a, b) => {
    const aKey = `${a.filterLocation}-${a.filterSize}`;
    const bKey = `${b.filterLocation}-${b.filterSize}`;
    return aKey.localeCompare(bKey);
  });
};

// Helper function to compare two filter arrays
const filtersEqual = (filters1, filters2) => {
  const norm1 = normalizeFiltersForComparison(filters1);
  const norm2 = normalizeFiltersForComparison(filters2);
  if (norm1.length !== norm2.length) return false;
  return norm1.every((f1, i) => {
    const f2 = norm2[i];
    return f1.filterLocation === f2.filterLocation && f1.filterSize === f2.filterSize;
  });
};

export const formatHvacLastChangedDate = (date) => {
  if (date === null || date === undefined || date === '') return null;
  try {
    const parsedDate = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return null;
    return parsedDate.toISOString().split('T')[0];
  } catch {
    return null;
  }
};

const normalizeDate = (date) => formatHvacLastChangedDate(date) || '';

export default function HvacFiltersSection({ hvacFilters = [], hvacFiltersLastChanged, onChange }) {
  const [filters, setFilters] = useState([]);
  const [lastChanged, setLastChanged] = useState('');
  const [customSizeFilterIds, setCustomSizeFilterIds] = useState(() => new Set());
  const onChangeRef = useRef(onChange);
  const lastSentRef = useRef({ filters: null, lastChanged: null });
  const isInitialMount = useRef(true);

  // Keep onChange ref up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Sync from props only when props actually change from what we last sent
  useEffect(() => {
    const normalizedFilters = hvacFilters && hvacFilters.length > 0
      ? hvacFilters.map(filter => ({
          id: filter.id || `temp-${Date.now()}-${Math.random()}`,
          filterLocation: filter.filterLocation || '',
          filterSize: filter.filterSize || '',
        }))
      : [];
    
    const normalizedDate = normalizeDate(hvacFiltersLastChanged);
    
    // Serialize for comparison
    const currentFiltersKey = JSON.stringify(normalizeFiltersForComparison(normalizedFilters));
    const lastSentFiltersKey = lastSentRef.current.filters 
      ? JSON.stringify(normalizeFiltersForComparison(lastSentRef.current.filters))
      : null;
    
    // Only update if props differ from what we last sent to parent
    // This means props changed from an external source (e.g., initial load or external update)
    const propsDifferFromLastSent = 
      currentFiltersKey !== lastSentFiltersKey ||
      lastSentRef.current.lastChanged !== normalizedDate;
    
    if (propsDifferFromLastSent) {
      setFilters(normalizedFilters);
      setLastChanged(normalizedDate);
      setCustomSizeFilterIds(getCustomSizeFilterIds(normalizedFilters));
    }
    
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hvacFilters, hvacFiltersLastChanged]);

  // Notify parent when user makes changes
  const notifyParent = (newFilters, newLastChanged) => {
    if (onChangeRef.current) {
      const dataToSend = {
        hvacFilters: newFilters,
        hvacFiltersLastChanged: newLastChanged || null,
      };
      onChangeRef.current(dataToSend);
      // Track what we sent so we can detect external prop changes
      lastSentRef.current = {
        filters: newFilters,
        lastChanged: newLastChanged || null,
      };
    }
  };

  const handleAddFilter = () => {
    const newFilter = {
      id: `temp-${Date.now()}-${Math.random()}`,
      filterLocation: '',
      filterSize: '',
    };
    const updatedFilters = [...filters, newFilter];
    setFilters(updatedFilters);
    notifyParent(updatedFilters, lastChanged);
  };

  const handleRemoveFilter = (index) => {
    const removedFilter = filters[index];
    const updatedFilters = filters.filter((_, i) => i !== index);
    setFilters(updatedFilters);
    if (removedFilter?.id) {
      setCustomSizeFilterIds((previousIds) => {
        const nextIds = new Set(previousIds);
        nextIds.delete(removedFilter.id);
        return nextIds;
      });
    }
    notifyParent(updatedFilters, lastChanged);
  };

  const handleFilterChange = (index, field, value) => {
    const updatedFilters = [...filters];
    updatedFilters[index] = {
      ...updatedFilters[index],
      [field]: value,
    };
    setFilters(updatedFilters);
    notifyParent(updatedFilters, lastChanged);
  };

  const handleFilterSizeSelect = (index, value) => {
    const filterId = filters[index]?.id;
    if (value === CUSTOM_FILTER_SIZE_VALUE) {
      if (filterId) {
        setCustomSizeFilterIds((previousIds) => {
          const nextIds = new Set(previousIds);
          nextIds.add(filterId);
          return nextIds;
        });
      }
      if (isStandardFilterSize(filters[index].filterSize)) {
        handleFilterChange(index, 'filterSize', '');
      }
      return;
    }
    if (filterId) {
      setCustomSizeFilterIds((previousIds) => {
        const nextIds = new Set(previousIds);
        nextIds.delete(filterId);
        return nextIds;
      });
    }
    handleFilterChange(index, 'filterSize', value);
  };

  const handleCustomFilterSizeChange = (index, value) => {
    const nextValue = value.slice(0, MAX_FILTER_SIZE_LENGTH);
    handleFilterChange(index, 'filterSize', nextValue);
  };

  const handleLastChangedChange = (e) => {
    const newDate = e.target.value;
    setLastChanged(newDate);
    notifyParent(filters, newDate);
  };

  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
      <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900">HVAC Filters</h3>
        <p className="mt-1 text-sm text-gray-500">
          Manage HVAC filter locations and sizes for this property
        </p>
      </div>
      <div className="p-6 space-y-6">
        {/* Last Changed Date */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Last Changed Date
          </label>
          <input
            type="date"
            value={lastChanged}
            onChange={handleLastChangedChange}
            className={baseInputStyles}
          />
          <p className="text-xs text-gray-500">
            Date when all filters were last changed. This will be used to automatically create work orders every 6 months.
          </p>
        </div>

        {/* Filters List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Filter Locations
            </label>
            <button
              type="button"
              onClick={handleAddFilter}
              className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200 transition-colors"
            >
              + Add Filter
            </button>
          </div>

          {filters.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <p className="text-sm text-gray-500">No filters added yet</p>
              <p className="text-xs text-gray-400 mt-1">Click "Add Filter" to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filters.map((filter, index) => {
                const isCustomSizeSelected = customSizeFilterIds.has(filter.id) || isCustomFilterSize(filter.filterSize);
                const selectedSizeValue = isCustomSizeSelected
                  ? CUSTOM_FILTER_SIZE_VALUE
                  : (filter.filterSize || '');
                return (
                <div key={filter.id} className="flex gap-3 items-start p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-700" htmlFor={`hvac-filter-location-${filter.id}`}>
                        Filter Location
                      </label>
                      <input
                        id={`hvac-filter-location-${filter.id}`}
                        type="text"
                        value={filter.filterLocation}
                        onChange={(e) => handleFilterChange(index, 'filterLocation', e.target.value)}
                        placeholder="e.g., Main Unit, Upstairs, Living Room"
                        className={baseInputStyles}
                        aria-label="Filter location"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-700" htmlFor={`hvac-filter-size-${filter.id}`}>
                        Filter Size
                      </label>
                      <select
                        id={`hvac-filter-size-${filter.id}`}
                        value={selectedSizeValue}
                        onChange={(e) => handleFilterSizeSelect(index, e.target.value)}
                        className={selectStyles}
                        aria-label="Filter size"
                      >
                        <option value="">Select size</option>
                        {STANDARD_FILTER_SIZES.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                        <option value={CUSTOM_FILTER_SIZE_VALUE}>Custom size...</option>
                      </select>
                      {isCustomSizeSelected && (
                        <>
                          <input
                            id={`hvac-filter-custom-size-${filter.id}`}
                            type="text"
                            value={filter.filterSize}
                            onChange={(e) => handleCustomFilterSizeChange(index, e.target.value)}
                            placeholder="e.g., 15x20x1"
                            maxLength={MAX_FILTER_SIZE_LENGTH}
                            className={baseInputStyles}
                            aria-label="Custom filter size"
                          />
                          <p className="text-xs text-gray-500">
                            Enter the exact filter size for this property.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFilter(index)}
                    className="mt-6 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md border border-red-200 transition-colors"
                    aria-label="Remove filter"
                  >
                    Remove
                  </button>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

